import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { ComponentStore } from '@ngrx/component-store';
import { HttpUtilitiesService, IGenericCrudRequestConstructionParameters } from '@utilities/http-utilities.service';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { AdminUserGroupService } from '../admin-user-group.service';

interface IAdminUserGroupsState {
  data: IUserGroup[] | null;
  dataTotal: number;
  dataLoading: boolean;
  error: string | null;
}

const initialState: IAdminUserGroupsState = {
  data: null,
  dataLoading: false,
  dataTotal: 0,
  error: null,
};

@Injectable()
export class AdminUserGroupsTabStore extends ComponentStore<IAdminUserGroupsState> {
  public readonly data$ = this.selectSignal((state: IAdminUserGroupsState) => state.data);
  public readonly dataTotal$ = this.selectSignal((state: IAdminUserGroupsState) => state.dataTotal);
  public readonly dataLoading$ = this.selectSignal((state: IAdminUserGroupsState) => state.dataLoading);
  public readonly error$ = this.selectSignal((state: IAdminUserGroupsState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          data: this.get().data ?? [],
          dataLoading: false,
          dataTotal: this.get().data ? this.get().dataTotal : 0,
          error: errorMessage,
        });

        return EMPTY;
      }),
    );

  public readonly fetchUserGroups = this.effect((params$: Observable<IGenericCrudRequestConstructionParameters>) => {
    return params$.pipe(
      tap(() => this.patchState({ dataLoading: true, error: null })),
      switchMap((params) => {
        const httpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.adminUserGroupService.getUserGroups(httpParams).pipe(
          tap((response: IApiResponse<IUserGroup[]>) => {
            this.patchState({
              data: response.data ?? [],
              dataLoading: false,
              dataTotal: response.meta?.total ?? 0,
            });
          }),
          this.handleError('Failed to load user groups'),
        );
      }),
    );
  });

  constructor(
    private readonly adminUserGroupService: AdminUserGroupService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super(initialState);
  }
}
